import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Header, Input, Card, Image, Grid, Segment, Icon, Loader, Label } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { Profile } from '../../app/models/Profile';
import FollowButton from '../profiles/FollowButton';
import { useTranslation } from 'react-i18next';
import { sportOptions } from '../../app/common/options/sportOptions';
import FriendRequestButton from './FriendRequestButton';

export default observer(function FindFriends() {
    const { friendStore } = useStore();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [isMobile, setIsMobile] = useState(false);

    // Get localized sport names
    const getLocalizedSportName = (sportId: string) => {
        const sport = sportOptions.find(opt => opt.value === sportId);
        if (sport) {
            return t(`sports.${sport.label.toLowerCase()}`) || sport.label;
        }
        return null;
    };

    // Check if device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth <= 768;
            setIsMobile(isTouchDevice && isSmallScreen);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedTerm(searchTerm);
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    // Trigger search when debounced term changes
    useEffect(() => {
        if (debouncedTerm && debouncedTerm.length >= 3) {
            friendStore.searchFriends(debouncedTerm);
        } else {
            friendStore.clearSearch();
        }
    }, [debouncedTerm, friendStore]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    return (
        <Container style={{ marginTop: isMobile ? '1em' : '2em', padding: isMobile ? '0 0.5em' : '0' }}>
            <Header as='h1' icon textAlign='center' style={{ marginBottom: isMobile ? '1em' : '1.5em' }}>
                <Icon name='user plus' circular />
                <Header.Content style={{ fontSize: isMobile ? '1.5em' : '2em', marginBottom: '0.5em' }}>
                    {t('findFriends.title')}
                </Header.Content>
                <Header.Subheader style={{ fontSize: isMobile ? '0.9em' : '1em', marginTop: '0.75em' }}>
                    {t('findFriends.subtitle')}
                </Header.Subheader>
            </Header>

            <Segment style={{ padding: isMobile ? '0.8em' : '1em' }}>
                <Input
                    fluid
                    icon='search'
                    iconPosition='left'
                    placeholder={isMobile ? t('findFriends.searchPlaceholderShort') : t('findFriends.searchPlaceholder')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    size={isMobile ? 'large' : 'large'}
                />
            </Segment>

            {friendStore.searching && (
                <Segment textAlign='center' style={{ minHeight: isMobile ? '150px' : '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Loader active inline='centered'>{t('findFriends.searching')}</Loader>
                </Segment>
            )}

            {!friendStore.searching && searchTerm.length > 0 && searchTerm.length < 3 && (
                <Segment textAlign='center' color='blue' style={{ padding: isMobile ? '1em' : '1.5em' }}>
                    <Icon name='info circle' size='large' />
                    <p style={{ fontSize: isMobile ? '0.9em' : '1em' }}>
                        {t('findFriends.minCharactersMessage')}
                    </p>
                </Segment>
            )}

            {!friendStore.searching && searchTerm.length >= 3 && friendStore.searchResults.length === 0 && (
                <Segment textAlign='center' color='yellow' style={{ padding: isMobile ? '1em' : '1.5em' }}>
                    <Icon name='search' size='large' />
                    <p style={{ fontSize: isMobile ? '0.9em' : '1em' }}>
                        {t('findFriends.noResults', { searchTerm })}
                    </p>
                </Segment>
            )}

            {!friendStore.searching && friendStore.searchResults.length > 0 && (
                <>
                    <Header as='h3' dividing style={{ fontSize: isMobile ? '1.2em' : '1.5em', margin: isMobile ? '1em 0 0.5em 0' : '1.5em 0 1em 0' }}>
                        <Icon name='search' />
                        {t('findFriends.searchResults', { count: friendStore.searchResults.length })}
                    </Header>

                    {isMobile ? (
                        // Mobile Layout - Stacked vertically
                        <div style={{ marginBottom: '1em' }}>
                            {friendStore.searchResults.map((friend) => {
                                const profile: Profile = {
                                    userID: friend.id,
                                    firstName: friend.firstName || '',
                                    lastName: friend.lastName || '',
                                    fullName: friend.fullName || '',
                                    username: '',
                                    image: friend.imageUrl || '/assets/user.png',
                                    following: friend.following ?? false,
                                    followersCount: 0,
                                    followingCount: 0,
                                    latitude: 0,
                                    longitude: 0,
                                    friendsCount:0,
                                    sports: friend.sports || []
                                };

                                return (
                                    <Card key={friend.id} fluid style={{ marginBottom: '0.8em' }}>
                                        <Card.Content style={{ padding: '1em' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8em' }}>
                                                <Link to={`/profiles/${friend.id}`} style={{ flexShrink: 0 }}>
                                                    <Image
                                                        src={friend.imageUrl || '/assets/user.png'}
                                                        circular
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </Link>
                                                <div style={{ marginLeft: '1em', flex: 1, minWidth: 0 }}>
                                                    <Link to={`/profiles/${friend.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                        <Card.Header style={{
                                                            fontSize: '1.1em',
                                                            marginBottom: '0.3em',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {friend.fullName}
                                                        </Card.Header>
                                                    </Link>
                                                    <Card.Meta style={{ fontSize: '0.85em' }}>
                                                        <Icon name='user' /> {t('findFriends.sportsEnthusiast')}
                                                    </Card.Meta>
                                                </div>
                                            </div>

                                            {/* Favorite Sports - Mobile */}
                                            {friend.sports && friend.sports.length > 0 && (
                                                <div style={{ marginBottom: '0.8em' }}>
                                                    <div style={{
                                                        fontSize: '0.85em',
                                                        fontWeight: '600',
                                                        color: '#00b5ad',
                                                        marginBottom: '0.4em'
                                                    }}>
                                                        {t('findFriends.favoriteSports')}
                                                    </div>
                                                    <Label.Group size='small'>
                                                        {friend.sports.slice(0, 3).map((sport) => {
                                                            const localizedName = getLocalizedSportName(sport.id);
                                                            return localizedName ? (
                                                                <Label key={sport.id} color='teal' style={{ marginBottom: '3px' }}>
                                                                    {localizedName}
                                                                </Label>
                                                            ) : null;
                                                        })}
                                                        {friend.sports.length > 3 && (
                                                            <Label basic color='teal'>
                                                                +{friend.sports.length - 3}
                                                            </Label>
                                                        )}
                                                    </Label.Group>
                                                </div>
                                            )}

                                            <div style={{ marginTop: '0.5em' }}>
                                                <div style={{ marginBottom: '0.5em' }}>
                                                    <FollowButton profile={profile} />
                                                </div>
                                                <div>
                                                    <FriendRequestButton userId={friend.id} />
                                                </div>
                                            </div>
                                        </Card.Content>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        // Desktop Layout - Grid
                        <Card.Group itemsPerRow={1} stackable>
                            {friendStore.searchResults.map((friend) => {
                                const profile: Profile = {
                                    userID: friend.id,
                                    firstName: friend.firstName || '',
                                    lastName: friend.lastName || '',
                                    fullName: friend.fullName || '',
                                    username: '',
                                    image: friend.imageUrl || '/assets/user.png',
                                    following: friend.following ?? false,
                                    followersCount: 0,
                                    followingCount: 0,
                                    latitude: 0,
                                    longitude: 0,
                                    friendsCount:0,
                                    sports: friend.sports || []
                                };

                                return (
                                    <Card key={friend.id} fluid>
                                        <Card.Content>
                                            <Grid verticalAlign='middle'>
                                                <Grid.Row>
                                                    <Grid.Column width={3} textAlign='center'>
                                                        <Link to={`/profiles/${friend.id}`}>
                                                            <Image
                                                                src={friend.imageUrl || '/assets/user.png'}
                                                                circular
                                                                size='small'
                                                                style={{
                                                                    width: '80px',
                                                                    height: '80px',
                                                                    objectFit: 'cover',
                                                                    margin: '0 auto',
                                                                    cursor: 'pointer'
                                                                }}
                                                            />
                                                        </Link>
                                                    </Grid.Column>
                                                    <Grid.Column width={10}>
                                                        <Link to={`/profiles/${friend.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <Card.Header style={{ fontSize: '1.3em', marginBottom: '0.5em', cursor: 'pointer' }}>
                                                                {friend.fullName}
                                                            </Card.Header>
                                                        </Link>
                                                        <Card.Meta style={{ marginBottom: '0.5em' }}>
                                                            <Icon name='user' /> {t('findFriends.sportsEnthusiast')}
                                                        </Card.Meta>

                                                        {/* Favorite Sports - Desktop */}
                                                        {friend.sports && friend.sports.length > 0 && (
                                                            <div style={{ marginTop: '0.8em' }}>
                                                                <div style={{
                                                                    fontSize: '0.9em',
                                                                    fontWeight: '600',
                                                                    color: '#00b5ad',
                                                                    marginBottom: '0.4em'
                                                                }}>
                                                                    {t('findFriends.favoriteSports')}
                                                                </div>
                                                                <Label.Group size='medium'>
                                                                    {friend.sports.slice(0, 5).map((sport) => {
                                                                        const localizedName = getLocalizedSportName(sport.id);
                                                                        return localizedName ? (
                                                                            <Label key={sport.id} color='teal' style={{ marginBottom: '5px' }}>
                                                                                {localizedName}
                                                                            </Label>
                                                                        ) : null;
                                                                    })}
                                                                    {friend.sports.length > 5 && (
                                                                        <Label basic color='teal'>
                                                                            +{friend.sports.length - 5} {t('findFriends.more')}
                                                                        </Label>
                                                                    )}
                                                                </Label.Group>
                                                            </div>
                                                        )}
                                                    </Grid.Column>
                                                    <Grid.Column width={3} textAlign='center'>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                                            <div style={{ marginBottom: '0.8em', position: 'relative', zIndex: 1 }}>
                                                                <FollowButton profile={profile} />
                                                            </div>
                                                            <div style={{ position: 'relative', zIndex: 1 }}>
                                                                <FriendRequestButton userId={friend.id} />
                                                            </div>
                                                        </div>
                                                    </Grid.Column>
                                                </Grid.Row>
                                            </Grid>
                                        </Card.Content>
                                    </Card>
                                );
                            })}
                        </Card.Group>
                    )}
                </>
            )}

            {!searchTerm && !friendStore.searching && (
                <Segment placeholder textAlign='center' style={{
                    minHeight: isMobile ? '200px' : '300px',
                    padding: isMobile ? '2em 1em' : '3em 2em'
                }}>
                    <Header icon>
                        <Icon name='search' style={{ fontSize: isMobile ? '2em' : '3em' }} />
                        {t('findFriends.startSearching')}
                    </Header>
                    <p style={{ fontSize: isMobile ? '0.9em' : '1em', padding: isMobile ? '0 0.5em' : '0' }}>
                        {t('findFriends.startSearchingDescription')}
                    </p>
                </Segment>
            )}
        </Container>
    );
});